/**
 * Shared workflow schedule projection.
 *
 * Single source of truth for "when will each step run", used by BOTH the live
 * execution sheet (WorkflowExecutionSheet) and the Result tab (StepTable) so the
 * two stay consistent. Mirrors the audience-processor workers:
 * - email_campaign with businessHoursOnly sends in the next 9am–5pm window (in
 *   its timezone) and only hands off to the next play once it has sent;
 * - delays add their configured duration;
 * - the cursor tracks each step's HAND-OFF time (for an email, its send time).
 */

import type { RunExecution } from '@/types/execution-results';

export interface DelayConfig { unit: 'days' | 'hours' | 'minutes'; duration: number }

/** Parse a delay step's configured duration. */
export const parseDelayConfig = (cad: Record<string, unknown> | null): DelayConfig | null => {
  if (!cad) return null;
  const config = cad.configuration as { unit?: string; duration?: number } | undefined;
  if (!config?.unit || !config?.duration) return null;
  if (!['days', 'hours', 'minutes'].includes(config.unit)) return null;
  return { unit: config.unit as DelayConfig['unit'], duration: config.duration };
};

/** Add a delay's duration to a base timestamp. */
export const computeDelayFireTime = (baseIso: string, config: DelayConfig): Date => {
  const base = new Date(baseIso);
  if (config.unit === 'days') base.setDate(base.getDate() + config.duration);
  else if (config.unit === 'hours') base.setHours(base.getHours() + config.duration);
  else base.setMinutes(base.getMinutes() + config.duration);
  return base;
};

/** "3 days" / "48 hours" label. */
export const formatDelayDuration = (config: { unit: string; duration: number }): string =>
  `${config.duration} ${config.unit}`;

/* ---------- timezone-aware business-hours projection ---------- */

/** The campaign's email scheduling timezone (falls back to the viewer's). */
export const getScheduleTimezone = (executions: RunExecution[]): string => {
  for (const e of executions) {
    const tz = (e.custom_action_details?.timezone as string | undefined) || undefined;
    if (tz) return tz;
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/** The viewer's local timezone — projections are DISPLAYED here. */
export const displayTimezone = (fallback = 'UTC'): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || fallback;
  } catch {
    return fallback;
  }
};

const tzHourWeekday = (date: Date, timeZone: string): { hour: number; weekday: number } => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', hour12: false, weekday: 'short' }).formatToParts(date);
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10) % 24;
    const wd: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const weekday = wd[parts.find((p) => p.type === 'weekday')?.value || 'Mon'] ?? 1;
    return { hour, weekday };
  } catch {
    return { hour: date.getUTCHours(), weekday: date.getUTCDay() };
  }
};

/** Snap a time to the next 9am–5pm Mon–Fri window in the given timezone. */
export const snapToBusinessHours = (date: Date, timeZone: string): Date => {
  let d = new Date(date);
  for (let i = 0; i < 10; i += 1) {
    const { hour, weekday } = tzHourWeekday(d, timeZone);
    if (weekday === 0 || weekday === 6) { d = new Date(d.getTime() + 24 * 3600_000); continue; }
    if (hour >= 17) { d = new Date(d.getTime() + 14 * 3600_000); continue; }
    if (hour < 9) { d = new Date(d.getTime() + (9 - hour) * 3600_000); continue; }
    break;
  }
  return d;
};

/** When this step hands off to the next play (email → its office-hours send). */
export const handoffTimeAfter = (start: Date, exec: RunExecution, timeZone: string): Date => {
  if (exec.play_type === 'email_campaign' && exec.custom_action_details?.businessHoursOnly === true) {
    return snapToBusinessHours(start, timeZone);
  }
  return start;
};

/**
 * Projected SEND time of an email that has ALREADY been dispatched (has
 * executed_at) but whose office-hours send window is still in the future — i.e.
 * "scheduled, will actually send at <time>". Returns null for emails that aren't
 * business-hours, haven't been dispatched yet (use buildScheduleMap for those —
 * their send depends on the delays before them), or have already sent.
 */
export const projectedSendTime = (exec: RunExecution, timeZone: string): Date | null => {
  if (exec.play_type !== 'email_campaign' || exec.custom_action_details?.businessHoursOnly !== true) return null;
  if (!exec.executed_at) return null;
  const send = snapToBusinessHours(new Date(exec.executed_at), timeZone);
  return send.getTime() > Date.now() ? send : null;
};

/**
 * Project the fire/run time for every step. The cursor tracks each step's
 * hand-off time; for a business-hours email that's its send time, so the next
 * step is projected when the email actually sends and the timeline is monotonic.
 */
export const buildScheduleMap = (executions: RunExecution[], timeZone: string): Map<number, Date> => {
  const map = new Map<number, Date>();
  const now = Date.now();
  let cursor: Date | null = null;
  const ordered = [...executions].sort((a, b) => a.sequence_order - b.sequence_order);
  for (const exec of ordered) {
    if (exec.executed_at) {
      const ran = new Date(exec.executed_at);
      map.set(exec.audience_play_id, ran);
      // A completed delay's executed_at is when the wait STARTED, not when it
      // hands off. The next step fires at the delay's scheduled time — use the
      // real backend value when present (deferred Service Bus enqueue time,
      // already business-hours adjusted), else fall back to executed_at + the
      // configured duration. Without this the cursor would drop the delay and
      // the next touch would project to the delay's start day ("sends today").
      if (exec.play_type === 'delay') {
        const dc = parseDelayConfig(exec.custom_action_details);
        cursor = exec.scheduled_handoff_at
          ? new Date(exec.scheduled_handoff_at)
          : (dc ? computeDelayFireTime(ran.toISOString(), dc) : ran);
      } else {
        cursor = handoffTimeAfter(ran, exec, timeZone);
      }
      continue;
    }
    if (!cursor) continue;
    const runAt: Date = cursor.getTime() < now ? new Date(now) : cursor;
    if (exec.play_type === 'delay') {
      const dc = parseDelayConfig(exec.custom_action_details);
      const fire = dc ? computeDelayFireTime(runAt.toISOString(), dc) : runAt;
      map.set(exec.audience_play_id, fire);
      cursor = fire;
    } else {
      const sendAt = handoffTimeAfter(runAt, exec, timeZone);
      map.set(exec.audience_play_id, sendAt);
      cursor = sendAt;
    }
  }
  return map;
};

/* ---------- display formatters (viewer-local) ---------- */

/** "Mon, Jun 2 · 9:00 AM" in a timezone. */
export const formatAbsolute = (date: Date, timeZone: string): string => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }).format(date).replace(',', '').replace(' at ', ' · ');
  } catch {
    return date.toLocaleString();
  }
};

/** "Jun 2" — date only. */
export const formatAbsoluteDay = (date: Date, timeZone: string): string => {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone, month: 'short', day: 'numeric' }).format(date);
  } catch {
    return date.toLocaleDateString();
  }
};

/** Short tz label, e.g. "BST". */
export const tzAbbrev = (date: Date, timeZone: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(date);
    return parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
};

/** Whether a step only dispatches during configured business hours. */
export const stepUsesBusinessHours = (exec: RunExecution): boolean => {
  const cad = exec.custom_action_details;
  if (!cad) return false;
  if (exec.play_type === 'email_campaign') return cad.businessHoursOnly === true;
  if (exec.play_type === 'heyreach_campaign') return cad.business_hours_only === true;
  return false;
};

/** IANA timezone configured on a business-hours step. */
export const getStepScheduleTimezone = (exec: RunExecution): string | undefined => {
  const cad = exec.custom_action_details;
  if (!cad) return undefined;
  const businessHours = cad.business_hours as { timezone?: string } | undefined;
  const emailHours = cad.businessHours as { timezone?: string } | undefined;
  return businessHours?.timezone || emailHours?.timezone || (cad.timezone as string | undefined);
};

/** True when now falls outside Mon–Fri 9:00–17:00 in the given timezone. */
export const isCurrentlyOutsideBusinessHours = (timeZone: string): boolean => {
  const { hour, weekday } = tzHourWeekday(new Date(), timeZone);
  if (weekday === 0 || weekday === 6) return true;
  return hour < 9 || hour >= 17;
};

/**
 * Estimated date for a pending step — relative hint + absolute day (viewer-local).
 * Empty string when the time is in the past (the step is imminent/overdue).
 */
export const formatEstimatedDate = (date: Date): string => {
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  if (diffMs <= 0) return '';
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  const abs = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  if (date.toDateString() === now.toDateString()) return `today · ${abs}`;
  if (diffDays <= 1) return `tomorrow · ${abs}`;
  if (diffDays <= 7) return `in ${diffDays}d · ${abs}`;
  return abs;
};

/** True when an ISO time is past by more than graceMs (default 1h — ignores normal delivery lag). */
const isOverdueIso = (iso: string | null | undefined, graceMs = 3_600_000): boolean =>
  !!iso && Date.now() - new Date(iso).getTime() > graceMs;

/**
 * A not-yet-run step is "overdue" when the completed delay before it had a real
 * hand-off time (scheduled_handoff_at) that has already passed — the deferred
 * message should have fired but the step hasn't run. Used to show "overdue"
 * instead of a reassuring projection clamped to "now".
 *
 * A pending heyreach_campaign step is exempt: audience-processor's connection-
 * acceptance gate (connectionAcceptanceGate.js) waits for the LinkedIn recipient to
 * accept a connection request before sending the follow-up, rechecking for up to
 * ~6 days, and deliberately never records an execution per retry (so its own dedup
 * logic keeps seeing "no execution yet" on every scheduled recheck) — this run
 * response has no field that distinguishes that from a genuine stall, so treating
 * it as overdue after the 1h grace is a false alarm on a completely healthy run.
 * Confirmed live 2026-08-15 (audience 6967). Backend mirrors this in
 * runHistory.service.js's isChainStalled (mb-server) so the top-of-panel "Stalled"
 * banner doesn't fire either.
 */
export const isStepOverdue = (exec: RunExecution, executions: RunExecution[]): boolean => {
  if (exec.executed_at) return false;
  if (exec.play_type === 'heyreach_campaign') return false;
  const ordered = [...executions].sort((a, b) => a.sequence_order - b.sequence_order);
  const idx = ordered.findIndex((e) => e.audience_play_id === exec.audience_play_id);
  if (idx < 0) return false;
  const prevDelay = [...ordered.slice(0, idx)].reverse()
    .find((e) => e.play_type === 'delay' && e.scheduled_handoff_at);
  return isOverdueIso(prevDelay?.scheduled_handoff_at);
};
